import { Component, OnInit } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { OtpService } from '@app/services/authenticators/otp.service';
import { CertificateService } from '@app/services/certificates/certificate.service';
import { ErrorService } from '@app/services/error.service';
import { UserCertificate } from '@app/store/models/certificate.model';
import { UserSession } from '@app/store/models/session.model';
import { GlobalState } from '@app/store/store';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { LocalStorageService } from 'ngx-webstorage';
import { tap } from 'rxjs';

@UntilDestroy()
@Component({
  selector: 'app-first-emis',
  templateUrl: './first-emis.component.html',
  styleUrls: ['./first-emis.component.scss'],
  standalone: false,
})
export class FirstEmisPageComponent implements OnInit {
  constructor(
    private certificateService: CertificateService,
    private otpService: OtpService,
    private localStorageService: LocalStorageService,
    private router: Router,
    private store: Store<GlobalState>,
    private toasterService: ToastrService,
    private translateService: TranslateService,
    private errorService: ErrorService
  ) {}

  showStep2 = false;
  showCheck = false;
  showInfo = true;
  showPin = true;
  session: UserSession | undefined;
  certificate: UserCertificate | undefined;
  puk = '';
  loading = false;
  isPkBox = false;
  isEseal = false;
  isTrissUser = false;
  isExternalCA = false;
  showPuk = true;
  automatic = false;

  otpSent = false;

  hardwareOrVasco = false;

  form = new UntypedFormGroup({});
  formOptions: FormlyFormOptions = {
    formState: { submitAttempted: false },
  };

  model = { puk: '', newPin: '', confirmPin: '', otp: '' };
  fields: FormlyFieldConfig[] = [];

  createFields(hardwareOrVasco: boolean = false): FormlyFieldConfig[] {
    const pinLenght = hardwareOrVasco ? 6 : 8;
    console.log('🚀 ~ FirstEmisPageComponent ~ createFields ~ pinLenght', {
      hardwareOrVasco,
      pinLenght,
    });

    const fields: FormlyFieldConfig[] = [
      {
        validators: {
          validation: [
            {
              name: 'SAME_PIN_PUK',
              options: { errorPath: 'newPin' },
            },
            {
              name: 'SAME_PIN',
              options: { errorPath: 'confirmPin' },
            },
          ],
        },
        fieldGroupClassName: 'formly-group ic1-row',
        fieldGroup: [
          {
            key: 'newPin',
            type: 'ic1-text',
            className: 'ic1-col-12 ic1-col-lg-6',
            expressions: {
              hide: (field: FormlyFieldConfig) => {
                return !this.showPin;
              },
            },
            validators: {
              validation: ['PIN_PUK_VALIDATOR'],
            },

            props: {
              type: 'password',
              label: 'ACTIVATION_STEPS_PAGE.FIRST_EMIS_STEP.PIN_SIGN_LABEL',
              placeholder:
                'ACTIVATION_STEPS_PAGE.FIRST_EMIS_STEP.PIN_SIGN_PLACEHOLDER',

              minLength: 8,
              maxLength: 8,
              required: true,
              OnlyNumbers: 8,
            },
          },
          {
            key: 'confirmPin',
            type: 'ic1-text',
            className: 'ic1-col-12 ic1-col-lg-6',
            expressions: {
              hide: (field: FormlyFieldConfig) => {
                return !this.showPin;
              },
            },
            validators: {
              validation: ['PIN_PUK_VALIDATOR'],
            },

            props: {
              type: 'password',
              label:
                'ACTIVATION_STEPS_PAGE.FIRST_EMIS_STEP.CONFIRM_PIN_SIGN_LABEL',
              placeholder:
                'ACTIVATION_STEPS_PAGE.FIRST_EMIS_STEP.CONFIRM_PIN_SIGN_LABEL',
              minLength: 8,
              maxLength: 8,
              required: true,
              OnlyNumbers: 8,
            },
          },

          {
            key: 'puk',
            type: 'ic1-text',
            className: 'ic1-col-12 ic1-col-lg-6',
            expressions: {
              hide: (field: FormlyFieldConfig) => {
                return !this.showPuk;
              },
            },
            validators: {
              validation: ['PIN_PUK_VALIDATOR'],
            },

            props: {
              type: 'password',
              label: 'ACTIVATION_STEPS_PAGE.FIRST_EMIS_STEP.PIN_PUK_LABEL',
              placeholder:
                'ACTIVATION_STEPS_PAGE.FIRST_EMIS_STEP.PIN_PUK_PLACEHOLDER',
              description:
                'ACTIVATION_STEPS_PAGE.FIRST_EMIS_STEP.PIN_PUK_DESCRIPTION',
              minLength: 8,
              maxLength: 8,
              required: true,
              OnlyNumbers: 8,
            },
          },
          {
            key: 'otp',
            type: 'ic1-otp',
            className: 'ic1-col-12 ic1-col-lg-6 ',

            props: {
              type: 'password',
              label: 'COMMON.ACCORDION_OTP.LABEL',
              required: true,
              hardware: this.hardwareOrVasco,
              minLength: pinLenght,
              maxLength: pinLenght,
              OnlyNumbers: pinLenght,
            },
          },
        ],
      },
    ];

    return fields;
  }

  ngOnInit(): void {
    console.log('🚀 ~ FirstEmisPageComponent init');

    this.formOptions.showError = (field: FormlyFieldConfig) => {
      if (field.hide) return false;
      const key = field.key as string;
      // Confronti tra campi: visibili anche in digitazione (submitAttempted può essere false)
      if (
        key === 'newPin' &&
        field.formControl?.invalid &&
        field.formControl?.errors?.['SAME_PIN_PUK']
      ) {
        return true;
      }
      if (
        key === 'confirmPin' &&
        field.formControl?.invalid &&
        field.formControl?.errors?.['SAME_PIN']
      ) {
        return true;
      }
      const st = this.formOptions.formState as { submitAttempted?: boolean };
      if (!st?.submitAttempted) return false;
      return !!(field.formControl?.invalid);
    };

    this.form.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
      const st = this.formOptions.formState as { submitAttempted?: boolean };
      if (st?.submitAttempted) {
        st.submitAttempted = false;
      }
    });

    this.store
      .pipe(
        untilDestroyed(this),
        tap(store => {
          const fallbackSession =
            this.session || this.localStorageService.retrieve('session');
          const activeSession = store.session || fallbackSession;
          const fallbackCertificate =
            this.certificate || this.localStorageService.retrieve('certificate');
          const activeCertificate = store.certificate || fallbackCertificate;

          if (activeSession) {
            this.session = activeSession;
            this.isEseal = activeSession.eseal;
            this.automatic = activeSession.automatic;
            this.isTrissUser = activeSession?.trissUser;
            // Se è eSeal e non è TRISS allora non visualizzare il campo di input di PUK
            if (this.isEseal && !this.isTrissUser) {
              this.showPuk = false;
            }

            this.showStep2 = !activeSession?.needsForcePassword;

            if (!activeSession?.icssUser && !activeSession?.trissUser) {
              this.isPkBox = true;
            } else {
              this.isPkBox = false;
            }
          }
          if (activeCertificate) {
            this.certificate = activeCertificate;
            // IF necessaria per controllare che ci sia un dato esatto nel certificato sennò dà errore
            if (this.certificate.certId) {
              if (
                this.certificate.certId.length === 32 &&
                this.certificate.certificateStatus === 'REGISTERED'
              ) {
                this.isExternalCA = true;
              }
            }
            this.hardwareOrVasco =
              activeCertificate.otpType === 'HARDWARE' ||
              activeCertificate.otpType === 'VASCO' ||
              activeCertificate.otpType === 'EXTERNAL';

            if (!this.fields.length) {
              this.fields = this.createFields(this.hardwareOrVasco);
            }
          }
          if (activeSession && activeCertificate) {
            // se è FA e NON è un sigillo con CA esterna, allora non mostrare i campi di PIN
            if (this.automatic && !this.isExternalCA) {
              this.showPin = false;
            } else {
              this.showPin = true;
            }

            if (
              (!this.session?.needsForcePassword &&
                activeCertificate?.certificateInfo &&
                activeCertificate?.active &&
                activeCertificate.certificateStatus !== 'REGISTERED' &&
                !activeSession.trissUser) ||
              (activeCertificate.certificateStatus == 'EMITTED' &&
                activeSession.trissUser)
            ) {
              // certificato già attivo riportalo in homepage
              this.router.navigate(['/homepage']);
            }

            this.otpSent = this.localStorageService.retrieve('otpSent');

            // modificato condizione in needsForcePassword === false, invece di !needsForcePassword per evitare di entrare nella condizione quando needsForcePassword è null
            if (this.session?.needsForcePassword === false && !this.otpSent) {
              this.sendOtp();
              this.otpSent = true;
            }
          }
        })
      )
      .subscribe();
  }

  get formErrors() {
    return this.form.errors;
  }

  get stepNumber(): number {
    return 2;
  }

  onSubmit() {
    this.loading = true;
    // stop here if form is invalid
    if (this.form.invalid) {
      (this.formOptions.formState as { submitAttempted: boolean }).submitAttempted =
        true;
      this.form.markAllAsTouched();
      this.loading = false;
      return;
    }
    // Se il puk nella form esiste, allora sovrascrivi il valore di this.puk
    if (this.model.puk) this.puk = this.model.puk.trim();

    if (!this.isEseal) {
      if (this.session?.automatic && !this.isExternalCA) {
        const pin = this.puk;
        // se automatic, registered e pkbox aspettare che finisce il job per poter proseguire.
        if (
          this.certificate?.certificateStatus == 'REGISTERED' &&
          this.isPkBox
        ) {
          //apro la modale d'errore con l'errore segnalato
          this.errorService.openErrorModal(
            this.translateService.instant(
              'ACTIVATION_STEPS_PAGE.FIRST_EMIS_STEP.ERROR.REGISTERED_DESC'
            )
          );
        } else {
          this.certificateService
            //@ts-ignore
            .emitAutomaticCertificate(pin, this.model.otp.trim())
            .subscribe({
              next: async response => {
                console.log(
                  '🚀 ~ ForcePWDComponent ~ onSubmit ~ response',
                  response
                );

                this.loading = false;
                // Se tutto va bene
                this.localStorageService.clear('puk');
                if (this.localStorageService.retrieve('needActivation')) {
                  let filteredNeedActivation = this.localStorageService
                    .retrieve('needActivation')
                    .filter(elem => elem !== this.certificate.certId);
                  this.localStorageService.store(
                    'needActivation',
                    filteredNeedActivation
                  );
                }

                this.showSuccess();
              },
              error: error => {
                console.error(error);
                //this.clearForm();
                this.loading = false;
              },
              complete: () => {
                this.loading = false;
              },
            });
        }
      } else {
        this.certificateService
          //@ts-ignore
          .emitCertificate(
            this.puk,
            this.model.newPin.trim(),
            this.model.otp.trim()
          )
          .subscribe({
            next: async response => {
              console.log(
                '🚀 ~ ForcePWDComponent ~ onSubmit ~ response',
                response
              );
              // Se tutto va bene
              this.localStorageService.clear('puk');
              if (this.localStorageService.retrieve('needActivation')) {
                let filteredNeedActivation = this.localStorageService
                  .retrieve('needActivation')
                  .filter(elem => elem !== this.certificate.certId);
                this.localStorageService.store(
                  'needActivation',
                  filteredNeedActivation
                );
              }

              this.showSuccess();
            },
            error: error => {
              console.error(error);
              //this.clearForm();
              this.loading = false;
            },
            complete: () => {
              this.loading = false;
            },
          });
      }
    } else {
      if (!this.isTrissUser) {
        this.certificateService
          //@ts-ignore
          .emitEsealCertificate(this.model.newPin.trim(), this.model.otp.trim())
          .subscribe({
            next: async response => {
              // Se tutto va bene
              this.localStorageService.clear('puk');
              if (this.localStorageService.retrieve('needActivation')) {
                let filteredNeedActivation = this.localStorageService
                  .retrieve('needActivation')
                  .filter(elem => elem !== this.certificate.certId);
                this.localStorageService.store(
                  'needActivation',
                  filteredNeedActivation
                );
              }

              this.showSuccess();
            },
            error: error => {
              console.error(error);
              //this.clearForm();
              this.loading = false;
            },
            complete: () => {
              this.loading = false;
            },
          });
      } else {
        this.certificateService
          //@ts-ignore
          .emitEsealTrissCertificate(
            this.model.newPin.trim(),
            this.model.puk.trim(),
            this.model.otp.trim()
          )
          .subscribe({
            next: async response => {
              // Se tutto va bene
              this.localStorageService.clear('puk');
              if (this.localStorageService.retrieve('needActivation')) {
                let filteredNeedActivation = this.localStorageService
                  .retrieve('needActivation')
                  .filter(elem => elem !== this.certificate.certId);
                this.localStorageService.store(
                  'needActivation',
                  filteredNeedActivation
                );
              }

              this.showSuccess();
            },
            error: error => {
              console.error(error);
              //this.clearForm();
              this.loading = false;
            },
            complete: () => {
              this.loading = false;
            },
          });
      }
    }
  }

  showSuccess() {
    this.showStep2 = false;
    this.showCheck = true;
    this.toasterService.success(
      this.translateService.instant(
        'ACTIVATION_STEPS_PAGE.FIRST_EMIS_STEP.SUCCESS.DESC'
      ),
      this.translateService.instant(
        'ACTIVATION_STEPS_PAGE.FIRST_EMIS_STEP.SUCCESS.TITLE'
      ),
      {
        positionClass: 'toast-top-right',
      }
    );

    // serve per richiamare i getter di session e certificate nella homepage
    this.localStorageService.store('refreshData', true);

    setTimeout(() => {
      this.loading = false;
      this.router.navigate(['/homepage']);
    }, 5000);
  }

  sendOtp() {
    this.otpService.generateCode().subscribe({
      next: async response => {
        // Se tutto va bene
      },
      error: error => {
        console.error(error);
      },
      complete: () => {},
    });
  }

  clearForm() {
    this.form.reset();
    (this.formOptions.formState as { submitAttempted: boolean }).submitAttempted =
      false;
    this.otpService.resetOtpInputVal(true);

    this.showPin = true;
    if (this.isEseal && !this.isTrissUser) {
      this.showPuk = false;
    } else {
      this.needsPuk();
    }

    if (this.session?.automatic && !this.isExternalCA) {
      this.showPin = false;
    }
  }

  needsPuk() {
    this.showPuk = true;
    this.localStorageService.clear('puk');
  }
}



